'use client'

import { useEffect, useRef, useState, useTransition } from 'react'

import type { CreatorPick } from '@/lib/creator'
import type { LineResult } from '@/lib/line-result'
import { createLineWrites } from '@/lib/line-writes'
import { type OrderLine, type StoredLine, toOrderLine, toPick } from '@/lib/order-line'
import { CART_EVENT } from '@/lib/use-cart-count'
import {
  addLineAction,
  clearOrderAction,
  discardCartAction,
  dropLineAction,
  patchLineAction,
} from '@/server/order-lines'

/**
 * The order's lines, and every way to change them.
 *
 * The server is the only writer, so this holds no truth of its own — it holds
 * what the person is DOING to the server's truth, which is a different thing
 * and the reason the old `CartProvider` could not simply be pointed at a
 * database. Three rules make it work:
 *
 * **Every action answers with the whole list.** Never a delta. Two tabs on one
 * basket is the normal case here, and a client that patches its own state from
 * a partial answer converges on "whoever wrote last" instead of on the server.
 *
 * **A keystroke paints immediately; the write settles.** Typing into a date or
 * a stepper edits local state on the same frame and schedules ONE write per
 * line after a pause. A session that used to be twenty-odd POSTs is six to ten.
 *
 * **A server list is accepted only when nothing is still being typed.** An
 * answer that arrives mid-edit describes a basket the person has already moved
 * past; taking it would delete the keystrokes that have not been sent yet.
 *
 * ── WHY NOT `useOptimistic` ──────────────────────────────────────────────
 * Because of the second rule. An optimistic value lives exactly as long as the
 * transition that set it, and a debounced write has not started when that
 * transition ends — so every settled edit would snap back to the old value for
 * the third of a second before its own request went out. `useOptimistic` fits
 * an action dispatched on the same tick as its update; this screen is the case
 * where the update deliberately precedes the action.
 */

/** How long a line waits after the last keystroke before it is written. */
const SETTLE_MS = 400

const MERGED = 'Ta sama pozycja już była w zamówieniu — połączyliśmy je.'

export type OrderLinesApi = {
  lines: OrderLine[]
  /** Is there a row on the server at all? Empty basket, kept cookie — still yes. */
  stored: boolean
  /** A write is in flight. Drives the "zapisuję" hint, never a spinner over the list. */
  pending: boolean
  /** A refusal from the server, or a note about what it did. Shown once, live. */
  notice: string | null
  add: (offeringSlug: string, pick: CreatorPick) => void
  patch: (lineId: string, next: Partial<CreatorPick>) => void
  drop: (lineId: string) => void
  clear: () => void
  /** Erase the row AND the cookie. Article 17, for somebody with no account. */
  discard: () => void
  /**
   * Close the debounce: send everything still waiting and resolve once the
   * server has answered for it.
   *
   * For anything that READS the basket row rather than the screen — which today
   * means the checkout, and it is not optional there. See `line-writes.ts`.
   */
  flush: () => Promise<void>
  /** The basket became an order — forget it here, without asking the server. */
  reset: () => void
  dismiss: () => void
}

export function useOrderLines(
  initial: StoredLine[],
  initiallyStored: boolean,
  /**
   * Something the SERVER did to the basket before this page painted — today,
   * lines whose offering left the offer. It seeds the same banner an action's
   * refusal uses, and is dismissed the same way, because from the reader's side
   * they are the same event: the basket is not what they left it as.
   */
  initialNotice: string | null = null,
): OrderLinesApi {
  const [lines, setLines] = useState(() => initial.map(toOrderLine))
  const [stored, setStored] = useState(initiallyStored)
  const [seen, setSeen] = useState(initial)
  const [notice, setNotice] = useState<string | null>(initialNotice)
  const [pending, start] = useTransition()

  /**
   * The list as of the last change, readable synchronously.
   *
   * `patch` has to read the CURRENT line before merging a field into it, and
   * two handlers can fire before React renders again — a chip and a date, a
   * ladder's "Biorę 8" landing next to a stepper. Reading `lines` there gives
   * both of them the value from the last paint, so the second one silently
   * overwrites the first. State a handler must read before the next render
   * needs a ref beside it.
   */
  const latest = useRef(lines)
  /** Everything the server has not been told yet. Created once, never replaced. */
  const [writes] = useState(() => createLineWrites(SETTLE_MS))

  // Re-base when the SERVER sends a different list — a fresh navigation, or the
  // page re-rendering under us. Done during render rather than in an effect:
  // an effect would paint the stale list for one frame first.
  if (seen !== initial) {
    setSeen(initial)
    const rebased = initial.map(toOrderLine)
    latest.current = rebased
    setLines(rebased)
  }

  /**
   * The last edit, saved even though nothing is going to be alive to see it.
   *
   * A server action does not survive the page: closing the tab, switching apps
   * on a phone or navigating away kills the request, so a change that was still
   * settling is simply lost — and the person comes back to a basket that quietly
   * forgot the date they picked. `keepalive` is the one thing a browser promises
   * to deliver after the page is gone, and a route handler is the only way to
   * use it. That is the entire reason `/api/koszyk` accepts a POST.
   *
   * Not `flush()`, which is the same intent for a page that will still be here
   * to see the answer: nothing can be awaited on the way out, so the leftovers
   * go as one fire-and-forget POST instead of as server actions.
   *
   * Also runs on unmount, which is the common case: a soft navigation off the
   * creator fires no `pagehide` at all.
   */
  useEffect(() => {
    const pendingLines = latest

    const abandon = () => {
      const ids = writes.waiting()
      if (ids.length === 0) return
      writes.cancelAll()

      const edits = ids.flatMap((lineId) => {
        const line = pendingLines.current.find((row) => row.lineId === lineId)
        return line ? [{ lineId, pick: toPick(line) }] : []
      })
      if (edits.length === 0) return

      void fetch('/api/koszyk', {
        method: 'POST',
        keepalive: true,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ edits }),
        // A basket that could not be saved is not worth an error in a console
        // nobody is looking at — the page is on its way out.
      }).catch(() => {})
    }

    const onHidden = () => {
      if (document.visibilityState === 'hidden') abandon()
    }
    document.addEventListener('visibilitychange', onHidden)
    window.addEventListener('pagehide', abandon)

    return () => {
      document.removeEventListener('visibilitychange', onHidden)
      window.removeEventListener('pagehide', abandon)
      abandon()
    }
  }, [writes])

  // The badge lives in the header, on every page, and has no other way to hear
  // about this. One event, one integer — see `/api/koszyk` for the read side.
  useEffect(() => {
    const count = lines.reduce((sum, line) => sum + line.pick.qty, 0)
    window.dispatchEvent(new CustomEvent(CART_EVENT, { detail: count }))
  }, [lines])

  const commit = (next: OrderLine[]) => {
    latest.current = next
    setLines(next)
  }

  const accept = (result: LineResult) => {
    if (!result.ok) {
      setNotice(result.error)
      return
    }
    setNotice(result.mergedInto ? MERGED : null)
    if (writes.waiting().length === 0) commit(result.lines.map(toOrderLine))
  }

  /**
   * One request, its answer folded back in, and the button kept honest while it
   * is in the air. The call is made before the transition rather than inside
   * it, so the promise exists to be handed to `LineWrites` — awaiting it inside
   * is what keeps `pending` true for as long as it takes.
   */
  const dispatch = (call: Promise<LineResult>): Promise<LineResult> => {
    start(async () => accept(await call))
    return call
  }

  return {
    lines,
    stored,
    pending,
    notice,
    dismiss: () => setNotice(null),
    flush: () => writes.settled(),

    add: (offeringSlug, pick) => {
      // The first add is what creates the row — and therefore what makes the
      // erasure control below mean something.
      setStored(true)
      writes.track(dispatch(addLineAction(toPick({ offeringSlug, pick }))))
    },

    patch: (lineId, next) => {
      const current = latest.current.find((line) => line.lineId === lineId)
      if (!current) return
      const line = { ...current, pick: { ...current.pick, ...next } }
      commit(latest.current.map((row) => (row.lineId === lineId ? line : row)))
      writes.schedule(lineId, () => dispatch(patchLineAction(lineId, toPick(line))))
    },

    drop: (lineId) => {
      writes.cancel(lineId)
      commit(latest.current.filter((line) => line.lineId !== lineId))
      writes.track(dispatch(dropLineAction(lineId)))
    },

    clear: () => {
      writes.cancelAll()
      commit([])
      writes.track(dispatch(clearOrderAction()))
    },

    discard: () => {
      writes.cancelAll()
      commit([])
      setStored(false)
      writes.track(dispatch(discardCartAction()))
    },

    /**
     * Local only, and that is the point: the checkout already converted the row
     * and dropped the cookie in the same breath as writing the order. Calling
     * `clearOrderAction` here would look for a basket that no longer exists and
     * put "nie udało się zapisać zmiany" on top of a successful order.
     */
    reset: () => {
      writes.cancelAll()
      commit([])
      setStored(false)
      setNotice(null)
    },
  }
}
